import React from 'react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useNavigate } from 'react-router-dom';
import { useAgents } from '../../hooks/useAgents';
import { 
  MessageSquare, 
  BarChart3, 
  Target, 
  Users, 
  PenTool,
  Clock
} from 'lucide-react';

interface RecentConversationsProps {
  projectId?: string | null;
}

const agentIcons = {
  data: BarChart3,
  marketing: Target,
  strategy: Target,
  persona: Users,
  content: PenTool
};

const agentColors = {
  data: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
  marketing: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
  strategy: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
  persona: 'bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400',
  content: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400'
};

export function RecentConversations({ projectId }: RecentConversationsProps) {
  const navigate = useNavigate();
  const { useConversations } = useAgents();
  
  // Get recent conversations, filtered by project if specified
  const { data: conversations, isLoading } = useConversations({
    projectId: projectId || undefined,
    limit: 5
  });

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInHours = Math.floor((now.getTime() - time.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    return `${Math.floor(diffInHours / 24)}d ago`;
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="flex items-start space-x-3 p-3">
              <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Use real conversations if available, otherwise fall back to empty state
  const conversationsToShow = conversations?.conversations || [];

  // Additional safety check to ensure conversationsToShow is an array
  if (!Array.isArray(conversationsToShow)) {
    console.warn('conversationsToShow is not an array:', conversationsToShow);
    return (
      <div className="text-center py-8">
        <MessageSquare className="w-8 h-8 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
        <p className="text-sm text-gray-500 dark:text-gray-400">Unable to load conversations</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {conversationsToShow.map((conversation: any) => {
        const Icon = agentIcons[conversation.agentType as keyof typeof agentIcons] || MessageSquare;
        const colorClass = agentColors[conversation.agentType as keyof typeof agentColors] || 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300';
        
        return (
          <div
            key={conversation.id}
            className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
            onClick={() => navigate(`/agents/${conversation.agentType}/conversation/${conversation.id}`)}
          >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorClass}`}>
              <Icon className="w-4 h-4" />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {conversation.title}
                </h4>
                <div className="flex items-center space-x-2">
                  <Badge variant="secondary" className="text-xs">
                    {conversation.messageCount} messages
                  </Badge>
                  <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                    <Clock className="w-3 h-3 mr-1" />
                    {formatTimeAgo(conversation.lastMessageAt || conversation.createdAt)}
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 truncate">
                {conversation.lastMessage || 'No messages yet'}
              </p>
            </div>
          </div>
        );
      })}
      
      {conversationsToShow.length === 0 && (
        <div className="text-center py-8">
          <MessageSquare className="w-8 h-8 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {projectId ? 'No conversations for this project yet' : 'No conversations yet'}
          </p>
          <Button 
            size="sm" 
            className="mt-3"
            onClick={() => navigate('/agents')}
          >
            Start your first conversation
          </Button>
        </div>
      )}
    </div>
  );
}